import UploadRounded from "@mui/icons-material/UploadRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
} from "@mui/material";
import { uploadFile } from "@bw/shared/api";
import { useState, type ChangeEvent } from "react";
import { hasSafeAvatar } from "../avatar";

const ALLOWED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const visuallyHiddenInput = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

interface ProfilePhotoUploadFieldProps {
  value?: string | null;
  onChange: (url: string) => void;
  disabled?: boolean;
  validationMessage?: string;
}

/** One device-upload path for onboarding and profile editing; never URL entry. */
export function ProfilePhotoUploadField({
  value,
  onChange,
  disabled = false,
  validationMessage,
}: ProfilePhotoUploadFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const previewUrl = hasSafeAvatar(value) ? value : null;

  async function handleSelected(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file || uploading || disabled) return;

    setUploadError(null);
    if (!(ALLOWED_AVATAR_TYPES as readonly string[]).includes(file.type)) {
      setUploadError("Choose an image file (JPEG, PNG, or WebP).");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setUploadError("That image is too large. Choose one under 5 MB.");
      return;
    }

    setUploading(true);
    try {
      onChange(await uploadFile(file, "avatars"));
    } catch {
      setUploadError("We couldn't upload that photo. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <FormControl component="fieldset" error={Boolean(validationMessage)}>
      <FormLabel component="legend">Profile photo (optional)</FormLabel>
      <Stack
        spacing={1.5}
        sx={{
          mt: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <Avatar
          src={previewUrl ?? undefined}
          alt={previewUrl ? "Profile photo preview" : undefined}
          sx={{ width: 64, height: 64, bgcolor: "action.selected" }}
        >
          {previewUrl ? null : <PersonRounded aria-hidden="true" />}
        </Avatar>
        <Stack direction="row" spacing={1}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={disabled || uploading}
            startIcon={
              uploading ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  aria-hidden="true"
                />
              ) : (
                <UploadRounded aria-hidden="true" />
              )
            }
          >
            {uploading
              ? "Uploading…"
              : previewUrl
                ? "Replace photo"
                : "Upload photo"}
            <Box
              component="input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleSelected}
              disabled={disabled || uploading}
              sx={visuallyHiddenInput}
            />
          </Button>
          {value ? (
            <Button
              type="button"
              variant="text"
              size="small"
              disabled={disabled || uploading}
              onClick={() => {
                setUploadError(null);
                onChange("");
              }}
            >
              Remove photo
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {uploadError ? (
        <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
          {uploadError}
        </Alert>
      ) : null}
      {validationMessage ? (
        <FormHelperText sx={{ mx: 0 }}>{validationMessage}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
